import React, { useEffect, useState } from 'react';
import { Calendar, dayjsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { IslamicEvent, apiMethods } from '../services/api';
import { Modal, Button, Form, Input, DatePicker, Select, message, theme, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { toHijriWithOffset, toGregorianWithOffset } from '../utils/hijriAdjustment';
import HijriDateInput from './HijriDateInput';
import ScheduleGrid from './ScheduleGrid';

// Custom styles for dual calendar
const dualCalendarStyles = `
  .custom-date-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 4px 2px;
    position: relative;
  }
  
  .custom-date-header .gregorian-date {
    font-size: 16px;
    font-weight: 600;
    color: #000;
    line-height: 1.2;
    margin-bottom: 4px;
  }
  
  .custom-date-header .hijri-date {
    font-size: 11px;
    color: #666;
    line-height: 1;
    white-space: nowrap;
    text-align: center;
    margin-top: 2px;
    direction: rtl;
  }
  
  .rbc-off-range .custom-date-header .gregorian-date {
    color: #ccc;
  }
  
  .rbc-off-range .custom-date-header .hijri-date {
    color: #ddd;
  }
  

`;

// Inject styles
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('dual-calendar-styles');
  if (existingStyle) {
    existingStyle.remove();
  }
  const styleElement = document.createElement('style');
  styleElement.id = 'dual-calendar-styles';
  styleElement.textContent = dualCalendarStyles;
  document.head.appendChild(styleElement);
}

const localizer = dayjsLocalizer(dayjs);


const HIJRI_MONTHS_ARABIC = [
  'محرم', 'صفر', 'ربيع الأول', 'ربيع الثاني', 
  'جمادى الأولى', 'جمادى الثانية', 'رجب', 'شعبان', 
  'رمضان', 'شوال', 'ذو القعدة', 'ذو الحجة'
];

// Custom date header component with Hijri offset support
const CustomDateHeader = ({ date }: any) => {
  const [hijriDate, setHijriDate] = React.useState<any>(null);
  const gregorianDate = new Date(date);
  
  React.useEffect(() => {
    const loadHijriDate = async () => {
      try {
        const hijri = await toHijriWithOffset(
          gregorianDate.getFullYear(), 
          gregorianDate.getMonth() + 1, 
          gregorianDate.getDate()
        );
        setHijriDate(hijri);
      } catch (error) {
        console.warn('Error loading Hijri date for calendar header:', error);
        // Display empty instead of fallback to avoid inconsistency
        setHijriDate(null);
      }
    };
    
    loadHijriDate();
  }, [gregorianDate.getTime()]);
  
  return (
    <div className="custom-date-header">
      <div className="gregorian-date">{gregorianDate.getDate()}</div>
      <div className="hijri-date">
        {hijriDate ? `${hijriDate.hd} ${HIJRI_MONTHS_ARABIC[hijriDate.hm - 1]}` : '...'}
      </div>
    </div>
  );
};



const Events: React.FC = () => {
  theme.useToken();
  const [events, setEvents] = useState<IslamicEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<IslamicEvent | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const loadEvents = async () => {
    setLoading(true);
    try {
      const fetchedEvents = await apiMethods.getEvents();
      setEvents(fetchedEvents);
    } catch (error) {
      console.error('Error loading events:', error);
      message.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    if (isCreateModalVisible) {
      // Reset form completely when create modal opens
      form.resetFields();
      form.setFieldsValue({
        title: '',
        description: '',
        greg_date: undefined,
        hijri_date: undefined,
        type: 'special_day',
        visibilitySchedule: null,
      });
    }
  }, [isCreateModalVisible, form]);

  useEffect(() => {
    if (!isModalVisible) {
      // Reset form when view/edit modal closes
      form.resetFields();
      setIsEditing(false);
      setSelectedEvent(null);
    }
  }, [isModalVisible, form]);

  const convertHijriToGregorian = async (hijriDate: string): Promise<Date> => {
    // Parse Hijri date (format: MM-DD)
    const [month, day] = hijriDate.split('-').map(Number);
    
    // Get current Hijri year to find the appropriate corresponding date
    const today = new Date();
    let currentHijriYear: number;
    
    try {
      const hijriToday = await toHijriWithOffset(today.getFullYear(), today.getMonth() + 1, today.getDate());
      currentHijriYear = hijriToday.hy;
    } catch (error) {
      console.warn('Error getting current Hijri year:', error);
      throw new Error('Failed to convert Hijri date - could not determine current Hijri year');
    }
    
    // Convert to Gregorian with offset
    try {
      const gregorianDate = await toGregorianWithOffset(currentHijriYear, month, day);
      // Create date at midnight local time to avoid timezone issues
      return new Date(gregorianDate.gy, gregorianDate.gm - 1, gregorianDate.gd);
    } catch (error) {
      console.warn('Error converting Hijri to Gregorian:', error);
      throw new Error('Failed to convert Hijri date to Gregorian');
    }
  };

  const formatHijriDate = (hijriDate: string): string => {
    if (!hijriDate) return '';
    
    // Parse Hijri date (format: MM-DD)
    const [month, day] = hijriDate.split('-').map(Number);
    
    // Get Arabic month name
    const arabicMonth = HIJRI_MONTHS_ARABIC[month - 1];
    
    // Return formatted Arabic date (day month)
    return `${day} ${arabicMonth}`;
  };

  const getEventDisplayDate = async (event: IslamicEvent): Promise<Date> => {
    if (event.greg_date) {
      // Parse the date string and create a date at midnight local time
      // This prevents timezone issues that could shift the event to the wrong day
      const dateString = event.greg_date.split('T')[0]; // Get just the date part (YYYY-MM-DD)
      const [year, month, day] = dateString.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed in JavaScript
    } else if (event.hijri_date) {
      return await convertHijriToGregorian(event.hijri_date);
    }
    throw new Error('No valid date found for event');
  };

  const [calendarEvents, setCalendarEvents] = useState<any[]>([]);

  // Load calendar events with Hijri offset applied
  useEffect(() => {
    const loadCalendarEvents = async () => {
      if (!events.length) {
        setCalendarEvents([]);
        return;
      }

      const processedEvents = await Promise.all(
        events.map(async (event) => {
          const displayDate = await getEventDisplayDate(event);
          
          // Debug logging to verify dates
          console.log(`Event: ${event.title}`);
          console.log(`  - Greg date: ${event.greg_date}`);
          console.log(`  - Hijri date: ${event.hijri_date}`);
          console.log(`  - Display date: ${displayDate.toDateString()}`);
          console.log(`  - Calendar slot: ${displayDate.getFullYear()}-${(displayDate.getMonth() + 1).toString().padStart(2, '0')}-${displayDate.getDate().toString().padStart(2, '0')}`);
          
          // Ensure the event spans the entire day to prevent timezone shifting
          const startDate = new Date(displayDate);
          const endDate = new Date(displayDate);
          endDate.setHours(23, 59, 59, 999); // End at the last moment of the day
          
          return {
            id: event.id,
            title: event.title,
            start: startDate,
            end: endDate,
            allDay: true,
            description: event.description,
            type: event.type,
            hijri_date: event.hijri_date,
            greg_date: event.greg_date
          };
        })
      );

      setCalendarEvents(processedEvents);
    };

    loadCalendarEvents();
  }, [events]);

  type EventFormType = Omit<IslamicEvent, 'id'> & { visibilitySchedule?: { [day: string]: number[] } | null };
  const handleCreateEvent = async (values: EventFormType) => {
    try {
      const payload = { ...values, visibility_schedule: values.visibilitySchedule ?? null };
      delete payload.visibilitySchedule;
      await apiMethods.createEvent(payload);
      message.success('Event created successfully');
      loadEvents();
      setIsCreateModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Error creating event:', error);
      message.error('Failed to create event');
    }
  };

  const handleUpdateEvent = async (values: EventFormType) => {
    try {
      if (!selectedEvent?.id) return;

      const payload = { ...values, visibility_schedule: values.visibilitySchedule ?? null };
      delete payload.visibilitySchedule;

      await apiMethods.updateEvent(selectedEvent.id, payload);
      message.success('Event updated successfully');
      loadEvents();
      setIsModalVisible(false);
      setIsEditing(false);
      setSelectedEvent(null);
      form.resetFields();
    } catch (error) {
      console.error('Error updating event:', error);
      message.error('Failed to update event');
    }
  };

  const handleDeleteEvent = async () => {
    try {
      if (!selectedEvent?.id) return;

      await apiMethods.deleteEvent(selectedEvent.id);
      message.success('Event deleted successfully');
      loadEvents();
      setIsModalVisible(false);
      setIsEditing(false);
      setSelectedEvent(null);
      form.resetFields();
    } catch (error) {
      console.error('Error deleting event:', error);
      message.error('Failed to delete event');
    }
  };

  const startEditing = () => {
    if (selectedEvent) {
      form.setFieldsValue({
        ...selectedEvent,
        visibilitySchedule: (selectedEvent as any).visibility_schedule || null,
      });
      setIsEditing(true);
    }
  };

  return (
    <div className="h-screen p-4">
      <div className="flex justify-between mb-4">
        <h1 className="text-2xl">Islamic Calendar</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            // Ensure form is completely clean before opening
            form.resetFields();
            setIsCreateModalVisible(true);
          }}
        >
          Add Event
        </Button>
      </div>

      <Spin spinning={loading}>
        <Calendar
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 'calc(100vh - 120px)' }}
          views={['month']}
          defaultView="month"
          components={{
            month: {
              dateHeader: CustomDateHeader
            }
          }}
          onSelectEvent={(event) => {
            const selectedEvent = events.find(e => e.id === event.id);
            if (selectedEvent) {
              setSelectedEvent(selectedEvent);
              setIsModalVisible(true);
            }
          }}
          tooltipAccessor={event => {
            const islamicEvent = events.find(e => e.id === event.id);
            if (!islamicEvent) return event.title;
            
            let tooltip = `${event.title}\n${islamicEvent.description}`;
            
            if (islamicEvent.greg_date && islamicEvent.hijri_date) {
              const gregDate = new Date(islamicEvent.greg_date).toLocaleDateString();
              tooltip += `\nGregorian: ${gregDate}\nHijri: ${islamicEvent.hijri_date}`;
            } else if (islamicEvent.greg_date) {
              const gregDate = new Date(islamicEvent.greg_date).toLocaleDateString();
              tooltip += `\nDate: ${gregDate}`;
            } else if (islamicEvent.hijri_date) {
              tooltip += `\nHijri: ${islamicEvent.hijri_date}`;
            }
            
            return tooltip;
          }}
          eventPropGetter={(event) => {
            const islamicEvent = events.find(e => e.id === event.id);
            if (!islamicEvent) return {};
            
            let backgroundColor = '#1890ff';
            switch (islamicEvent.type) {
              case 'holiday':
                backgroundColor = '#52c41a';
                break;
              case 'special_day':
                backgroundColor = '#1890ff';
                break;
              case 'historic':
                backgroundColor = '#722ed1';
                break;
              default:
                backgroundColor = '#1890ff';
            }
            
            return {
              style: {
                backgroundColor,
                borderColor: backgroundColor,
                color: 'white'
              }
            };
          }}
        />
      </Spin>

      <Modal
        title="Add New Event"
        open={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          form.resetFields();
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setIsCreateModalVisible(false);
            form.resetFields();
          }}>
            Cancel
          </Button>,
          <Button key="create" type="primary" onClick={() => form.submit()}>
            Create
          </Button>
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateEvent}
          initialValues={{
            type: 'special_day'
          }}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[
              { required: true, message: 'Please enter a title' },
              { max: 50, message: 'Title cannot be longer than 50 characters' }
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[
              { required: true, message: 'Please enter a description' },
              { max: 100, message: 'Description cannot be longer than 100 characters' }
            ]}
          >
            <Input.TextArea />
          </Form.Item>
          <Form.Item
            name="greg_date"
            label="Gregorian Date"
            validateTrigger={['onChange', 'onBlur']}
            dependencies={['hijri_date']}
            rules={[
              {
                validator: (_, value) => {
                  const hijriDate = form.getFieldValue('hijri_date');
                  if (!value && !hijriDate) {
                    return Promise.reject('Either Gregorian or Hijri date must be provided');
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="hijri_date"
            label="Hijri Date"
            tooltip="Select month and day"
            validateTrigger={['onChange', 'onBlur']}
            dependencies={['greg_date']}
            rules={[
              {
                validator: (_, value) => {
                  const gregDate = form.getFieldValue('greg_date');
                  if (!value && !gregDate) {
                    return Promise.reject('Either Gregorian or Hijri date must be provided');
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <HijriDateInput />
          </Form.Item>
          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: 'Please select an event type' }]}
          >
            <Select>
              <Select.Option value="holiday">Holiday</Select.Option>
              <Select.Option value="special_day">Special Day</Select.Option>
              <Select.Option value="historic">Historic</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="visibilitySchedule" label="Visibility Schedule">
            <ScheduleGrid />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={isEditing ? "Edit Event" : "Event Details"}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setIsEditing(false);
          setSelectedEvent(null);
          form.resetFields();
        }}
        footer={[
          <Button key="close" onClick={() => {
            setIsModalVisible(false);
            setIsEditing(false);
            setSelectedEvent(null);
            form.resetFields();
          }}>
            Close
          </Button>,
          !isEditing && (
            <Button key="delete" danger onClick={() => {
              Modal.confirm({
                title: 'Delete Event',
                content: `Are you sure you want to delete "${selectedEvent?.title}"?`,
                okText: 'Delete',
                okType: 'danger',
                cancelText: 'Cancel',
                onOk: handleDeleteEvent,
              });
            }}>
              Delete
            </Button>
          ),
          !isEditing && (
            <Button key="edit" type="primary" onClick={startEditing}>
              Edit
            </Button>
          ),
          isEditing && (
            <Button key="cancel" onClick={() => {
              setIsEditing(false);
              // Reset form to original event data
              form.resetFields();
              if (selectedEvent) {
                form.setFieldsValue({
                  title: selectedEvent.title,
                  description: selectedEvent.description,
                  hijri_date: selectedEvent.hijri_date,
                  greg_date: selectedEvent.greg_date ? dayjs(selectedEvent.greg_date) : undefined,
                  type: selectedEvent.type,
                  visibilitySchedule: (selectedEvent as any).visibility_schedule || null,
                });
              }
            }}>
              Cancel
            </Button>
          ),
          isEditing && (
            <Button key="save" type="primary" onClick={() => form.submit()}>
              Save
            </Button>
          )
        ]}
      >
        {selectedEvent && (
          isEditing ? (
            <Form
              form={form}
              layout="vertical"
              onFinish={handleUpdateEvent}
              initialValues={{
                title: selectedEvent.title,
                description: selectedEvent.description,
                greg_date: selectedEvent.greg_date ? dayjs(selectedEvent.greg_date) : undefined,
                hijri_date: selectedEvent.hijri_date,
                type: selectedEvent.type,
                visibilitySchedule: (selectedEvent as any).visibility_schedule || null,
              }}
            >
              <Form.Item
                name="title"
                label="Title"
                rules={[
                  { required: true, message: 'Please enter a title' },
                  { max: 100, message: 'Title cannot be longer than 100 characters' }
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="description"
                label="Description"
                rules={[
                  { required: true, message: 'Please enter a description' },
                  { max: 500, message: 'Description cannot be longer than 500 characters' }
                ]}
              >
                <Input.TextArea />
              </Form.Item>
              <Form.Item
                name="greg_date"
                label="Gregorian Date"
                validateTrigger={['onChange', 'onBlur']}
                dependencies={['hijri_date']}
                rules={[
                  {
                    validator: (_, value) => {
                      const hijriDate = form.getFieldValue('hijri_date');
                      if (!value && !hijriDate) {
                        return Promise.reject('Either Gregorian or Hijri date must be provided');
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                name="hijri_date"
                label="Hijri Date"
                tooltip="Select month and day"
                validateTrigger={['onChange', 'onBlur']}
                dependencies={['greg_date']}
                rules={[
                  {
                    validator: (_, value) => {
                      const gregDate = form.getFieldValue('greg_date');
                      if (!value && !gregDate) {
                        return Promise.reject('Either Gregorian or Hijri date must be provided');
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <HijriDateInput />
              </Form.Item>
              <Form.Item
                name="type"
                label="Type"
                rules={[{ required: true, message: 'Please select an event type' }]}
              >
                <Select>
                  <Select.Option value="holiday">Holiday</Select.Option>
                  <Select.Option value="special_day">Special Day</Select.Option>
                  <Select.Option value="historic">Historic</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="visibilitySchedule" label="Visibility Schedule">
                <ScheduleGrid />
              </Form.Item>
            </Form>
          ) : (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold mb-2">{selectedEvent.title}</h3>
                <p className="text-gray-600 mb-4">{selectedEvent.description}</p>
              </div>
              
              <div className="space-y-2">
                {selectedEvent.greg_date && (
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-700">Gregorian Date:</span>
                    <span>{new Date(selectedEvent.greg_date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</span>
                  </div>
                )}
                
                {selectedEvent.hijri_date && (
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-700">Hijri Date:</span>
                    <span className="arabic-text" style={{ direction: 'rtl' }}>
                      {formatHijriDate(selectedEvent.hijri_date)}
                    </span>
                  </div>
                )}
                
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-700">Type:</span>
                  <span className="capitalize">{selectedEvent.type.replace('_', ' ')}</span>
                </div>
              </div>
            </div>
          )
        )}
      </Modal>
    <div style={{ 
        marginTop: 24,
        borderTop: '1px solid #f0f0f0',
        paddingTop: 16,
        textAlign: 'center',
        color: 'rgba(0, 0, 0, 0.45)',
        fontSize: '14px'
    }}>
        True knowledge of Hijri dates relies on observation; any information provided here is for reference.
    </div>
    </div>
  );
};

export default Events; 